import { AxiosError } from 'axios'

import { api } from '~/utils/api'

import { insertToast } from '../toasts/services'
import {
  CreateDiscountDTO,
  CreatePlanDTO,
  PatchPlanDTO,
  PlanItem,
  UpdatePaymentCardDTO,
} from './schemas'
import {
  PagarmeInterval,
  PagarmeInvoiceStatus,
  PagarmeListInvoicesDTO,
  PagarmeListPlanDTO,
  PagarmeListDTO,
  PagarmeListSubscriptionsDTO,
  PagarmePaymentMethod,
  PaymentPlan,
  Subscription,
  PagarmeInvoice,
  Discount,
  SubscriptionStatus,
  PatchCardResponse,
  ApplyDiscountDTO,
  CreateSubscriptionDTO,
} from './types'

class AppError {
  constructor(
    public readonly errorMessage: string,
    public readonly errorCode: number = 500,
  ) {}
}

export async function getPlans(
  params: PagarmeListPlanDTO,
  abortSignal?: AbortSignal,
) {
  return api
    .get<PagarmeListDTO<PaymentPlan>>('/admin/sales/plans', {
      signal: abortSignal,
      params,
    })
    .then(({ data }) => {
      if (data.data) {
        return data
      }
    })
    .catch((e: AxiosError) => {
      console.log(e)
    })
}

export async function getPlan(planId: string, controller?: AbortController) {
  return api
    .get<PaymentPlan>(`/sales/plans/${planId}`, {
      signal: controller?.signal,
    })
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      if (e.request.status) {
        insertToast({
          title: 'Problema',
          message: e.response?.data.message,
          duration: 4,
          type: 'error',
        })
      }
      throw new AppError('Request Error', e.request.status)
    })
}

export async function getAvailablePlans(controller: AbortController) {
  return api
    .get<PaymentPlan[]>('/sales/plans', {
      signal: controller.signal,
    })
    .then(({ data }) => data)
    .catch(() => null)
}

export function getPlanDiscounts(planId: string) {
  return api
    .get<Discount[]>(`/admin/sales/plans/${planId}/discounts`)
    .then(({ data }) => data)
    .catch((e) => {
      throw e
    })
}

export async function getDiscountPlan(code: string) {
  return api
    .get<PaymentPlan>(`/personal-trainers/plans/code/${code}`)
    .then(({ data }) => data)
    .catch(() => null)
}

export async function getPersonalTrainerSubscription(
  abortCotnroller: AbortController,
) {
  return api
    .get<Subscription>('/personal-trainers/subscriptions', {
      signal: abortCotnroller.signal,
    })
    .then(({ data }) => data)
    .catch((e) => {
      console.error(e)
      return undefined
    })
}

export async function getSubscriptions(
  params: PagarmeListSubscriptionsDTO,
  abortSignal?: AbortSignal,
) {
  return api
    .get<PagarmeListDTO<Subscription>>('/admin/sales/subscriptions', {
      signal: abortSignal,
      params,
    })
    .then(({ data }) => data)
    .catch((e: AxiosError) => {
      console.log(e)
    })
}

export async function getInvoices(
  params: PagarmeListInvoicesDTO,
  abortSignal?: AbortSignal,
) {
  return api
    .get<PagarmeListDTO<PagarmeInvoice>>('/admin/sales/invoices', {
      signal: abortSignal,
      params,
    })
    .then(({ data }) => {
      if (data.data) {
        return data
      }
    })
    .catch((e: AxiosError) => {
      console.log(e)
    })
}

export async function patchPlan(planId: string, payload: PatchPlanDTO) {
  return api
    .patch<PaymentPlan>(`/admin/sales/plans/${planId}`, payload)
    .then(({ data }) => data)
    .catch((e: AxiosError) => {
      console.log(e)
    })
}

export async function deletePlan(planId: string, abortSignal?: AbortSignal) {
  return api
    .delete<PaymentPlan>(`/admin/sales/plans/${planId}`, {
      signal: abortSignal,
    })
    .then(({ data }) => data)
    .catch((e: AxiosError) => {
      console.log(e?.response?.data)
    })
}

export async function deleteSubscription(
  subscriptionId: string,
  abortSignal?: AbortSignal,
) {
  return api
    .delete(`/admin/sales/subscriptions/${subscriptionId}`, {
      signal: abortSignal,
    })
    .then((data) => data.status === 200)
    .catch((e: AxiosError) => {
      console.log(e?.response?.data)
      return false
    })
}

export async function deleteInvoice(
  invoiceId: string,
  abortSignal?: AbortSignal,
) {
  return api
    .delete(`/admin/sales/invoices/${invoiceId}`, {
      signal: abortSignal,
    })
    .then((data) => data.status === 200)
    .catch((e: AxiosError) => {
      console.log(e?.response?.data)
      return false
    })
}

export async function createPlan(payload: CreatePlanDTO) {
  return api
    .post<PaymentPlan>('/admin/sales/plans', payload)
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      throw new Error(e?.response?.data.message)
    })
}



export async function cancelSubscription(id: string) {
  return api
    .delete<Subscription>(`/personal-trainers/subscriptions/${id}/cancel`)
    .then(({ data }) => data)
    .catch((e: AxiosError) => {
      console.error(e.response?.data)
    })
}

export async function updateSubscriptionPaymentCard(
  subscriptionId: string,
  payload: UpdatePaymentCardDTO,
) {
  return api
    .patch<PatchCardResponse>(
      `/personal-trainers/subscriptions/${subscriptionId}/card`,
      payload,
    )
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      console.log(e.response?.data)
      insertToast({
        duration: 4,
        message: e.response?.data?.message,
        title: 'Erro!',
        type: 'error',
      })
    })
}

export async function listDiscounts(
  params: { page: number; size: number },
  controller: AbortController,
) {
  return api
    .get('/admin/sales/discounts', { params, signal: controller.signal })
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      insertToast({
        duration: 4,
        message: e.response?.data?.message,
        title: 'Erro!',
        type: 'error',
      })
    })
}

export async function createDiscount(payload: CreateDiscountDTO) {
  return api
    .post<Discount>('/admin/sales/discounts', payload)
    .then(({ data }) => {
      insertToast({
        duration: 3,
        title: 'Sucesso!',
        message: 'Desconto cadastrado.',
      })

      return data
    })
    .catch((e: AxiosError<APIError>) => {
      insertToast({
        duration: 4,
        message: e.response?.data?.message,
        title: 'Erro!',
        type: 'error',
      })
    })
}

export async function deleteDiscount(id: string) {
  return api
    .delete(`/admin/sales/discounts/${id}`)
    .then(() => {
      insertToast({
        duration: 3,
        title: 'Sucesso!',
        message: 'Desconto excluído.',
      })
    })
    .catch((e: AxiosError<APIError>) => {
      insertToast({
        duration: 4,
        message: e.response?.data?.message,
        title: 'Erro!',
        type: 'error',
      })
    })
}

export async function applyDiscount(
  { code, planId }: ApplyDiscountDTO,
  controller: AbortController,
) {
  return api
    .post(`/sales/discounts/${code}`, { planId }, { signal: controller.signal })
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      throw new Error(e.response?.data.message)
    })
}

export function numberToBRL(number: number) {
  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return formatter.format(number)
}

export function formatDiscount(type: Discount['type'], discountValue: number) {
  return type === 'flat'
    ? numberToBRL(discountValue / 100)
    : `${discountValue}%`
}

export function computeDiscountValue(
  type: Discount['type'],
  discountValue: number,
  cartValue: number,
) {
  return type === 'flat' ? discountValue : cartValue * (discountValue / 100)
}

export function sumTotalItemsValue(items: PlanItem[] | undefined) {
  if (!items) return 0

  let total = 0
  for (let i = 0; i < items.length; i++) {
    total += items[i].pricing_scheme.price * items[i].quantity
  }

  return total
}

export function translatePaymentMethods(
  paymentMethods?: PagarmePaymentMethod[],
) {
  return paymentMethods
    ? paymentMethods.map((p) => {
        switch (p) {
          case 'boleto':
            return 'boleto'
          case 'debit_card':
            return 'cartão de débito'
          case 'credit_card':
          default:
            return 'cartão de crédito'
        }
      })
    : []
}

export function translateSubscriptionStatus(status: SubscriptionStatus) {
  switch (status) {
    case 'canceled':
      return 'cancelada'
    case 'payment_pending':
      return 'pendente'
    case 'active':
    default:
      return 'ativa'
  }
}

export function translateInvoiceStatus(status: PagarmeInvoiceStatus) {
  switch (status) {
    case 'canceled':
      return 'cancelada'
    case 'pending':
      return 'pendente'
    case 'failed':
      return 'falha'
    case 'paid':
    default:
      return 'paga'
  }
}

export function translatePaymentInterval(
  interval?: PagarmeInterval,
  intervalCount = 0,
) {
  switch (interval) {
    case 'day':
      return intervalCount === 1
        ? 'uma vez a cada dia'
        : `uma vez a cada ${intervalCount} dias`
    case 'week':
      return intervalCount === 1
        ? 'uma vez a cada semana'
        : `uma vez a cada ${intervalCount} semanas`
    case 'year':
      return intervalCount === 1
        ? 'uma vez a cada ano'
        : `uma vez a cada ${intervalCount} anos`
    case 'month':
      return intervalCount === 1
        ? 'uma vez a cada mês'
        : `uma vez a cada ${intervalCount} meses`
    default:
      return ''
  }
}
